import { Box } from '@mantine/core';
import { SurveyLogicElement, SurveyLogicOperator, SurveyLogicRule } from '@vision/ui/interfaces/survey-logic-builder';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConditionValueBadge, ConditionValueToken } from '../ConditionValueToken';
import { getConditionPreviews } from './rule-accordion-item.utils';
import classes from './RuleConditionPreview.module.css';

const DEFAULT_GAP = 4;
const OVERFLOW_SAFETY_MARGIN = 1;

type Props = {
  rule: SurveyLogicRule;
  ruleIndex: number;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
};

type PreviewUnit = {
  id: string;
  value: string;
  valueTestId: string;
  conditionOperatorLabel?: string;
  conditionOperatorTestId?: string;
  valueOperator?: SurveyLogicOperator;
  valueOperatorLabel?: string;
  valueOperatorTestId?: string;
};

type PreviewUnitProps = {
  unit: PreviewUnit;
  hidden: boolean;
};

type PreviewLayout = {
  isOverflowing: boolean;
  visibleUnitCount: number;
};

function getGap(element: HTMLElement): number {
  const gap = Number.parseFloat(window.getComputedStyle(element).columnGap);
  return Number.isFinite(gap) ? gap : DEFAULT_GAP;
}

function getElementWidth(element: HTMLElement): number {
  return element.getBoundingClientRect().width || element.offsetWidth;
}

function RuleConditionPreviewUnit({ unit, hidden }: PreviewUnitProps) {
  return (
    <Box
      className={`${classes.previewUnit} ${hidden ? classes.previewUnitHidden : ''}`}
      aria-hidden={hidden || undefined}
      data-preview-token={true}
    >
      {unit.conditionOperatorLabel && (
        <Box component="span" className={classes.conditionOperator} data-testid={unit.conditionOperatorTestId}>
          {unit.conditionOperatorLabel}
        </Box>
      )}

      <ConditionValueToken
        value={unit.value}
        valueTooltipLabel={unit.value}
        valueTooltipOpenDelay={500}
        truncateValue={true}
        valueTestId={unit.valueTestId}
        operator={unit.valueOperator}
        operatorLabel={unit.valueOperatorLabel}
        operatorTestId={unit.valueOperatorTestId}
      />
    </Box>
  );
}

export function RuleConditionPreview({ rule, ruleIndex, elements, activeLanguageCode }: Props) {
  const { t } = useTranslation('page-survey-logic-builder');
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const overflowIndicatorRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PreviewLayout>({
    isOverflowing: false,
    visibleUnitCount: Number.POSITIVE_INFINITY,
  });

  const previewItems = useMemo(() => {
    const conditionGroupsOperator = rule.conditionGroupsOperator || 'and';
    const previews = getConditionPreviews(rule, elements, activeLanguageCode);

    return previews.map((preview, previewIndex) => {
      const previousPreview = previews[previewIndex - 1];
      const previewOperator = previousPreview
        ? previousPreview.groupId === preview.groupId
          ? preview.conditionOperator
          : conditionGroupsOperator
        : null;

      return {
        ...preview,
        previewOperator,
        previewOperatorLabel: previewOperator ? t(`operator.${previewOperator}`) : '',
        valueOperatorLabel: preview.valueOperator ? t(`operator.${preview.valueOperator}`) : '',
      };
    });
  }, [activeLanguageCode, elements, rule, t]);

  const previewUnits = useMemo(
    () =>
      previewItems.flatMap<PreviewUnit>((preview, previewIndex) =>
        preview.values.map((value, valueIndex) => ({
          id: `${preview.id}-value-${valueIndex}`,
          value,
          valueTestId: `text-rule-condition-option-${ruleIndex}-${previewIndex}-${valueIndex}`,
          ...(valueIndex === 0 && preview.previewOperator
            ? {
                conditionOperatorLabel: preview.previewOperatorLabel,
                conditionOperatorTestId: `text-rule-condition-preview-operator-${ruleIndex}-${previewIndex}`,
              }
            : {}),
          ...(valueIndex > 0 && preview.valueOperator
            ? {
                valueOperator: preview.valueOperator,
                valueOperatorLabel: preview.valueOperatorLabel,
                valueOperatorTestId: `section-rule-condition-option-operator-${ruleIndex}-${previewIndex}-${valueIndex}`,
              }
            : {}),
        })),
      ),
    [previewItems, ruleIndex],
  );

  const previewSummary = useMemo(
    () =>
      previewItems
        .map((preview) => {
          const valueSeparator = preview.valueOperatorLabel ? ` ${preview.valueOperatorLabel} ` : ' ';
          const values = preview.values.join(valueSeparator);
          const groupedValues = preview.values.length > 1 ? `(${values})` : values;

          return preview.previewOperatorLabel ? `${preview.previewOperatorLabel} ${groupedValues}` : groupedValues;
        })
        .join(' '),
    [previewItems],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    const overflowIndicator = overflowIndicatorRef.current;

    if (!container || !content || !overflowIndicator) {
      return;
    }

    const measure = () => {
      const containerWidth = container.clientWidth;

      if (containerWidth === 0) {
        setLayout((currentLayout) =>
          !currentLayout.isOverflowing && currentLayout.visibleUnitCount === previewUnits.length
            ? currentLayout
            : { isOverflowing: false, visibleUnitCount: previewUnits.length },
        );
        return;
      }

      const unitElements = Array.from(content.querySelectorAll<HTMLElement>('[data-preview-token]'));
      const unitGap = getGap(content);
      const totalUnitWidth = unitElements.reduce((total, unit) => total + getElementWidth(unit), 0);
      const totalGapWidth = Math.max(0, unitElements.length - 1) * unitGap;
      const nextIsOverflowing = totalUnitWidth + totalGapWidth > containerWidth;
      let nextVisibleUnitCount = previewUnits.length;

      if (nextIsOverflowing) {
        const availableWidth = Math.max(
          0,
          containerWidth - getElementWidth(overflowIndicator) - getGap(container) - OVERFLOW_SAFETY_MARGIN,
        );
        let usedWidth = 0;

        nextVisibleUnitCount = 0;

        for (const unitElement of unitElements) {
          const nextWidth = usedWidth + (nextVisibleUnitCount > 0 ? unitGap : 0) + getElementWidth(unitElement);

          if (nextWidth > availableWidth) {
            break;
          }

          usedWidth = nextWidth;
          nextVisibleUnitCount += 1;
        }
      }

      setLayout((currentLayout) => {
        if (
          currentLayout.isOverflowing === nextIsOverflowing &&
          currentLayout.visibleUnitCount === nextVisibleUnitCount
        ) {
          return currentLayout;
        }

        return { isOverflowing: nextIsOverflowing, visibleUnitCount: nextVisibleUnitCount };
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [previewUnits]);

  if (previewUnits.length === 0) {
    return null;
  }

  return (
    <Box ref={containerRef} className={classes.container} data-testid={`section-rule-condition-options-${ruleIndex}`}>
      <Box ref={contentRef} className={classes.units}>
        {previewUnits.map((unit, unitIndex) => (
          <RuleConditionPreviewUnit key={unit.id} unit={unit} hidden={unitIndex >= layout.visibleUnitCount} />
        ))}
      </Box>

      <ConditionValueBadge
        ref={overflowIndicatorRef}
        value="..."
        tooltipLabel={layout.isOverflowing ? previewSummary : undefined}
        className={layout.isOverflowing ? '' : classes.overflowIndicatorProbe}
        tabIndex={layout.isOverflowing ? 0 : -1}
        ariaHidden={!layout.isOverflowing}
        ariaLabel={layout.isOverflowing ? previewSummary : undefined}
        testId={layout.isOverflowing ? `text-rule-condition-overflow-${ruleIndex}` : undefined}
      />
    </Box>
  );
}
